import { useSignOut } from "../../hooks/use-sign-out";
import { ChartColumn, CirclePlay, Copy, EllipsisVertical, Link, LogOut, Pencil, Plus, QrCode, Trash2, X } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { getProgression, type GradeGroup } from "../../lib/quiz-module";
import { getPalette } from "../../lib/child-presets";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import type { ChildProfile } from "../../types";
import "./parent-dashboard.scss";

type FamilyInfo = { id: string; name: string; inviteCode: string; members: { id: string; name: string; email: string }[] };

type Props = {
  children: ChildProfile[];
  onChildCreated: (child: ChildProfile) => void;
  onChildUpdated: (child: ChildProfile) => void;
  onChildDeleted: (id: string) => void;
  onViewStats: (child: ChildProfile) => void;
};

export default function ParentDashboard({
  children,
  onChildCreated,
  onChildUpdated,
  onChildDeleted,
  onViewStats,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [gender, setGender] = useState<"boy" | "girl" | "">("");
  const [gradeGroup, setGradeGroup] = useState<GradeGroup>("3-5");
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [qrChild, setQrChild] = useState<ChildProfile | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const qrModalRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLButtonElement[]>([]);
  const closeQrModal = useCallback(() => setQrChild(null), []);
  useFocusTrap(qrModalRef, !!qrChild, closeQrModal);

  const { data: family } = useQuery<FamilyInfo>({
    queryKey: ["family"],
    queryFn: async () => {
      const res = await fetch("/api/family");
      if (!res.ok) throw new Error("Failed to load family");
      return res.json();
    },
    staleTime: 5 * 60_000,
  });

  const copyInviteCode = () => {
    if (!family) return;
    navigator.clipboard.writeText(family.inviteCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(null);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(null);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const items = menuItemsRef.current.filter(Boolean);
        const idx = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === "ArrowDown"
          ? (idx + 1) % items.length
          : (idx - 1 + items.length) % items.length;
        items[next]?.focus();
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    requestAnimationFrame(() => menuItemsRef.current[0]?.focus());
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  const activeProgression = getProgression(gradeGroup);

  const resetForm = () => {
    setName("");
    setGender("");
    setGradeGroup("3-5");
    setStep(1);
    setShowForm(false);
    setEditingId(null);
  };

  const startEdit = (child: ChildProfile) => {
    setEditingId(child.id);
    setName(child.name);
    setGender(child.gender);
    setGradeGroup(child.gradeGroup);
    setStep(child.currentStep);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);

    try {
      if (editingId) {
        const res = await fetch(`/api/children/${editingId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: name.trim(), gender, gradeGroup, currentStep: step }),
        });
        if (res.ok) onChildUpdated(await res.json());
      } else {
        const res = await fetch("/api/children", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: name.trim(), gender, gradeGroup, currentStep: step }),
        });
        if (res.ok) onChildCreated(await res.json());
      }
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("למחוק את הילד/ה? הפעולה לא ניתנת לביטול.")) return;
    const res = await fetch(`/api/children/${id}`, { method: "DELETE" });
    if (res.ok) onChildDeleted(id);
  };

  const copyLink = (child: ChildProfile) => {
    const url = `${window.location.origin}/play/${child.magicToken}`;
    navigator.clipboard.writeText(url);
  };

  const stepLabel = (s: number, gg: GradeGroup = "3-5") => {
    const prog = getProgression(gg);
    const cfg = prog.find((p) => p.step === s);
    return cfg ? `${s} (${cfg.cefrLevel})` : String(s);
  };

  const gradeGroupLabel = (gg: string) => gg === "1-2" ? "א-ב" : "ג-ה";

  const { logout: handleSignOut, error: logoutError, pending: loggingOut } = useSignOut();

  return (
    <main id="main-content" className="dashboard" dir="rtl" aria-label="ניהול ילדים">
      {logoutError && <p role="alert">{logoutError}</p>}

      {children.length > 0 && (
        <button className="btn-kids-home" onClick={() => { window.location.href = "/kids"; }}>
          <CirclePlay className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
          כניסה לילדים
        </button>
      )}

      {children.length === 0 && !showForm && (
        <p className="dashboard-empty">אין ילדים עדיין. הוסיפו ילד/ה כדי להתחיל!</p>
      )}

      <div className="child-list">
        {children.map((child) => {
          const palette = getPalette(child.colorPalette);
          return (
          <div key={child.id} className="child-card" style={{ borderColor: palette.primaryLight }}>
            {child.emoji && <span className="child-card-emoji">{child.emoji}</span>}
            <div className="child-card-info">
              <span className="child-card-name">{child.name}</span>
              <span className="child-card-step">
                כיתה {gradeGroupLabel(child.gradeGroup)} · שלב באוצר מילים {stepLabel(child.currentStep, child.gradeGroup)}
              </span>
            </div>
            <div className="child-card-menu-anchor" ref={menuOpen === child.id ? menuRef : undefined}>
              <button
                className="icon-btn"
                onClick={() => setMenuOpen(menuOpen === child.id ? null : child.id)}
                aria-label={`פעולות עבור ${child.name}`}
                aria-expanded={menuOpen === child.id}
                aria-haspopup="menu"
              >
                <EllipsisVertical className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
              </button>
              {menuOpen === child.id && (
                <div className="child-card-menu" role="menu" aria-label={`פעולות עבור ${child.name}`}>
                  <button role="menuitem" ref={(el) => { if (el) menuItemsRef.current[0] = el; }} className="child-card-menu-item" onClick={() => { onViewStats(child); setMenuOpen(null); }}>
                    <ChartColumn className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
                    התקדמות באוצר מילים
                  </button>
                  <button role="menuitem" ref={(el) => { if (el) menuItemsRef.current[1] = el; }} className="child-card-menu-item" onClick={() => { copyLink(child); setMenuOpen(null); }}>
                    <Link className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
                    העתק קישור
                  </button>
                  <button role="menuitem" ref={(el) => { if (el) menuItemsRef.current[2] = el; }} className="child-card-menu-item" onClick={() => { setQrChild(child); setMenuOpen(null); }}>
                    <QrCode className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
                    QR קוד
                  </button>
                  <button role="menuitem" ref={(el) => { if (el) menuItemsRef.current[3] = el; }} className="child-card-menu-item" onClick={() => { startEdit(child); setMenuOpen(null); }}>
                    <Pencil className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
                    ערוך
                  </button>
                  <button role="menuitem" ref={(el) => { if (el) menuItemsRef.current[4] = el; }} className="child-card-menu-item danger" onClick={() => { handleDelete(child.id); setMenuOpen(null); }}>
                    <Trash2 className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
                    מחק
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {showForm ? (
        <form className="child-form" onSubmit={handleSubmit} aria-label={editingId ? "עריכת ילד/ה" : "הוספת ילד/ה"}>
          <div className="form-field">
            <label className="form-label" htmlFor="child-name">שם</label>
            <input
              id="child-name"
              className="form-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="שם הילד/ה..."
              autoFocus
            />
          </div>

          <div className="form-field">
            <label className="form-label" id="gender-label">בן או בת?</label>
            <div className="gender-toggle" role="group" aria-labelledby="gender-label">
              <button
                type="button"
                className={`gender-chip ${gender === "boy" ? "active" : ""}`}
                aria-pressed={gender === "boy"}
                onClick={() => setGender(gender === "boy" ? "" : "boy")}
              >
                בן
              </button>
              <button
                type="button"
                className={`gender-chip ${gender === "girl" ? "active" : ""}`}
                aria-pressed={gender === "girl"}
                onClick={() => setGender(gender === "girl" ? "" : "girl")}
              >
                בת
              </button>
            </div>
          </div>

          <div className="form-field">
            <label className="form-label" id="grade-label">שכבת גיל</label>
            <div className="gender-toggle" role="group" aria-labelledby="grade-label">
              <button
                type="button"
                className={`gender-chip ${gradeGroup === "1-2" ? "active" : ""}`}
                aria-pressed={gradeGroup === "1-2"}
                onClick={() => { setGradeGroup("1-2"); setStep(1); }}
              >
                כיתות א-ב
              </button>
              <button
                type="button"
                className={`gender-chip ${gradeGroup === "3-5" ? "active" : ""}`}
                aria-pressed={gradeGroup === "3-5"}
                onClick={() => { setGradeGroup("3-5"); setStep(1); }}
              >
                כיתות ג-ה
              </button>
            </div>
          </div>

          <div className="form-field">
            <label className="form-label" id="step-label">שלב התחלתי באוצר מילים</label>
            <div className="step-grid" role="group" aria-labelledby="step-label">
              {activeProgression.map((s) => (
                <button
                  key={s.step}
                  type="button"
                  className={`step-chip ${s.step === step ? "active" : ""}`}
                  aria-pressed={s.step === step}
                  onClick={() => setStep(s.step)}
                >
                  {s.step}
                  <span className="step-level">{s.cefrLevel}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving || !name.trim()}>
              {saving ? "..." : editingId ? "שמור" : "הוסף"}
            </button>
            <button type="button" className="btn-secondary" onClick={resetForm}>
              ביטול
            </button>
          </div>
        </form>
      ) : (
        <button className="btn-add" onClick={() => setShowForm(true)}>
          <Plus className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
          הוסף ילד/ה
        </button>
      )}

      {qrChild && (
        <div className="qr-modal-overlay" onClick={() => setQrChild(null)} ref={qrModalRef}>
          <div className="qr-modal" role="dialog" aria-label={`QR קוד עבור ${qrChild.name}`} onClick={(e) => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={() => setQrChild(null)} aria-label="סגור">
              <X className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
            </button>
            <h2 className="qr-modal-title">קישור ל{qrChild.name}</h2>
            <div className="qr-modal-code">
              <QRCodeSVG
                value={`${window.location.origin}/play/${qrChild.magicToken}`}
                size={200}
                level="M"
                role="img"
                aria-label={`QR קוד לקישור של ${qrChild.name}`}
              />
            </div>
            <p className="qr-modal-hint">סרקו עם הטלפון כדי להתחיל לתרגל</p>
          </div>
        </div>
      )}
      <footer className="dashboard-utilities">
        {family && (
          <div className="family-info">
            <span className="family-name">{family.name}</span>
            <button className="invite-code-btn" onClick={copyInviteCode} title="העתק קוד הזמנה">
              <Copy className="brand-icon" style={{ fontSize: 16 }} aria-hidden="true" strokeWidth={2.4} />
              <span className="invite-code">{family.inviteCode}</span>
              {codeCopied && <span className="copied-badge">הועתק!</span>}
            </button>
          </div>
        )}
        <button className="dashboard-signout" onClick={handleSignOut} disabled={loggingOut} title="התנתקות" aria-label="התנתקות">
          <LogOut className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
        </button>
      </footer>
    </main>
  );
}
